import { useEffect, useMemo, useState } from 'react';
import { useSnoozeTask } from '@/state/data/task';
import { snoozeCeiling, SNOOZE_MAX_DAYS, templateCodeOf } from '@/logic/task';

// Snooze a task from the care-team digest + the patient's automated reminders.
// The modal's job is to leave nothing to guess: it states what snoozing does, and — more
// importantly — the two things it does NOT do (hide the task, move the due date), because those
// are exactly what a reader assumes. Spec: docs/specs/2026-08-06-task-snooze.md

const DAY = 86400000;
const iso = (d) => new Date(d).toISOString().slice(0, 10);
// `until` is INCLUSIVE everywhere it is read (WF4, WF60, the chip): the task is paused through
// that day and the reminders resume the day after — say both, never "comes back <until>".
// Guarded like `pretty`: a date input reads '' while its segments are incomplete or cleared, and
// an unguarded toISOString(NaN) THROWS during render — which took the whole patient file to the
// error boundary and lost the operator's typed reason.
const nextDay = (d) => {
  const t = Date.parse(d);
  return Number.isFinite(t) ? iso(t + DAY) : null;
};
// "1 month" is the server cap, not a calendar month: WF17 clamps anything longer and the browser
// never sees the clamp, so the wake date shown here must be the one that lands.
const PRESETS = [
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: SNOOZE_MAX_DAYS },
];

// What a snooze does depends on whose task it is. A patient task is still the patient's to do —
// we stop chasing, the task stays on their phone.
const EFFECTS = {
  staff: [
    { icon: '🔕', text: 'Hidden from the 8 AM care-team digest' },
    { icon: '📵', text: 'No reminders sent to the patient' },
  ],
  patient: [
    { icon: '📵', text: 'The patient won’t be texted or emailed about it' },
    { icon: '📱', text: 'Still visible in the patient’s app — we just stop chasing' },
  ],
};
// The device-setup tasks are patient tasks whose snooze ALSO silences the digest's activation
// nag for that patient (WF60 reads them; S-93 C8) — the patient copy must say so for those.
const DEVICE_CHAIN = new Set(['onb_device_setup', 'onb_device_setup_scale']);
const DIGEST_DEVICE_ROW = { icon: '🔕', text: 'The 8 AM care-team digest stops flagging the device setup' };
const pretty = (d) => {
  const t = Date.parse(d);
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    : d;
};

export default function SnoozeTaskModal({ task, withingsUserId, actor, onClose }) {
  // ONE clock read per open. Reading Date.now() per render froze `max` at mount while each
  // preset recomputed it, so a modal left open across UTC midnight disabled the largest preset
  // (now exactly the cap) with a filing-deadline tooltip on a task that has no deadline.
  const [nowMs] = useState(() => Date.now());
  const today = iso(nowMs);
  const ceiling = snoozeCeiling(task);                       // billing deadline, or null
  const max = useMemo(() => {
    const cap = iso(nowMs + SNOOZE_MAX_DAYS * DAY);          // WF17 caps here regardless
    return ceiling && ceiling < cap ? ceiling : cap;
  }, [ceiling, nowMs]);
  // The patient DOWNLOADS a task their app is allowed to show, which is `visible_to_patient` —
  // `assigned_to_type` only coincides with it across today's 85 seed templates, and WF17 can
  // write either field. Gate the copy on the wider predicate.
  const isPatientTask = task?.visible_to_patient === true || task?.assigned_to_type === 'patient';
  const effects = isPatientTask
    ? [...EFFECTS.patient, ...(DEVICE_CHAIN.has(templateCodeOf(task)) ? [DIGEST_DEVICE_ROW] : [])]
    : EFFECTS.staff;

  const [until, setUntil] = useState(() => {
    const d = iso(nowMs + 7 * DAY);
    return d > max ? max : d;
  });
  const [reason, setReason] = useState('');
  const snooze = useSnoozeTask();

  // Escape + body scroll lock. CAPTURE + stopPropagation is required: the patient drawer has its
  // own window-level Escape listener that navigates it closed (router.tsx).
  useEffect(() => {
    const onKey = (e) => { if (e.key !== 'Escape') return; e.stopPropagation(); onClose(); };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [onClose]);

  const canSubmit = reason.trim().length > 0 && until >= today && until <= max && !snooze.isPending;
  const submit = () => {
    if (!canSubmit) return;
    snooze.mutate(
      { taskId: task.task_id, until, reason: reason.trim(), withingsUserId, actor },
      { onSuccess: () => onClose({ snoozedUntil: until, reason: reason.trim() }) },
    );
  };

  return (
    <div className="modal-overlay" onClick={() => onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="header3">Snooze this task</h2>
          <button className="modal-close" aria-label="Close" onClick={() => onClose()}>×</button>
        </header>

        <div className="modal-body">
          <div style={{ margin: '0 0 16px' }}>
            <div className="header4" style={{ margin: 0 }}>{task?.title}</div>
            {task?.due_at && (
              <div className="detail1 text-secondary" style={{ marginTop: 2 }}>
                {`Due ${pretty(task.due_at)}`}
              </div>
            )}
          </div>

          <div className="snooze-effects">
            {effects.map((e) => (
              <div key={e.text} className="snooze-effects__row"><span aria-hidden>{e.icon}</span> {e.text}</div>
            ))}
            <div className="snooze-effects__row snooze-effects__row--keep"><span aria-hidden>📌</span> Due date unchanged — stays on the board</div>
          </div>

          {ceiling && (
            <div className="error-banner" style={{ marginBottom: 16 }}>
              ⚠️ Billing deadline — can’t be snoozed past <strong>{pretty(ceiling)}</strong>.
            </div>
          )}

          <div className="rx-field">
            <label className="eyebrow" htmlFor="snooze-until">Snooze until</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {PRESETS.map((p) => {
                const d = iso(nowMs + p.days * DAY);
                const disabled = d > max;
                return (
                  <button
                    key={p.label}
                    type="button"
                    className={until === d ? 'btn-primary' : 'btn-secondary'}
                    disabled={disabled}
                    title={disabled ? 'Past the filing deadline' : undefined}
                    onClick={() => setUntil(d)}
                  >
                    {p.label}
                  </button>
                );
              })}
              <input
                id="snooze-until"
                className="rx-input"
                type="date"
                value={until}
                min={today}
                max={max}
                onChange={(e) => setUntil(e.target.value)}
                style={{ width: 'auto', flexShrink: 0 }}
              />
            </div>
            <p className="detail1" style={{ color: 'var(--wi-text-secondary)', margin: 0 }}>
              {nextDay(until)
                ? `Paused through ${pretty(until)} — reminders resume ${pretty(nextDay(until))}`
                : 'Pick a date to see when the reminders resume'}
            </p>
          </div>

          <div className="rx-field" style={{ marginTop: 16 }}>
            <label className="eyebrow" htmlFor="snooze-reason">Reason (required)</label>
            <textarea
              id="snooze-reason"
              className="rx-input"
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. patient on holiday until the 19th"
            />
            <p className="detail1" style={{ color: 'var(--wi-text-secondary)', margin: '4px 0 0' }}>
              Visible to your team on the task. The care-team Chat digest reports only a count.
              {isPatientTask && ' The patient’s app downloads the task record — write it as if they could read it.'}
            </p>
          </div>

          {snooze.error && <div className="error-banner">{String(snooze.error)}</div>}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={() => onClose()}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={!canSubmit}>
            {snooze.isPending ? 'Snoozing…' : 'Snooze'}
          </button>
        </div>
      </div>
    </div>
  );
}
